import { useState, useMemo, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import { formatTimestamp, formatEnv, formatService, cleanMessage } from "./formatters";
import {
  tokens,
  EMPTY,
  SEVERITY,
  severityForLevel,
  severityForCode,
  levelLabel,
} from "./tokens";
import LogDetailDrawer from "./LogDetailDrawer";
import LogDetailModal from "./LogDetailModal";
import { LogEntry, LogMetadata } from "./types";

type Order = "asc" | "desc";

interface ColumnDef {
  field: string;
  label: string;
  width?: number;
  sortable: boolean;
}

// Base columns (Log Monitor). A "Count" column is inserted after "message" when
// message-grouping is enabled (Error Monitor) — see `columns` below.
const COLUMNS: ColumnDef[] = [
  { field: "timestamp", label: "Timestamp", width: 160, sortable: true },
  { field: "level", label: "Level", width: 72, sortable: true },
  { field: "service", label: "Service", width: 92, sortable: true },
  { field: "environment", label: "Env", width: 64, sortable: true },
  { field: "message", label: "Message", sortable: false },
  { field: "errorCode", label: "Error Code", width: 104, sortable: true },
  { field: "supplier", label: "Supplier", width: 92, sortable: true },
  { field: "filteredTags", label: "Tags", width: 184, sortable: false },
];

const COUNT_COLUMN: ColumnDef = { field: "count", label: "Count", width: 72, sortable: true };

// One rendered row: a representative log, how many logs it stands for, and the
// full list of occurrences (shown in the modal). count/logs.length are 1 when
// grouping is off; >1 collapses identical messages.
interface DisplayRow {
  log: LogEntry;
  count: number;
  logs: LogEntry[];
}

function cellValue(log: LogEntry, field: string): string | number {
  const m: LogMetadata = log.metadata || {};
  switch (field) {
    case "timestamp":
      return log.timestamp || "";
    case "level":
      return log.level || "";
    case "service":
      return log.service || "";
    case "environment":
      return log.environment || "";
    case "errorCode":
      return (m.statusCode ?? m.errorCode ?? "") as string | number;
    case "supplier":
      return log.supplier || "";
    default:
      return "";
  }
}

// Sort key for a row — count is a row-level field, everything else comes from the log.
function rowValue(row: DisplayRow, field: string): string | number {
  if (field === "count") return row.count;
  return cellValue(row.log, field);
}

// Em-dash placeholder for empty cells (muted).
function EmptyCell() {
  return <span style={{ color: tokens.textTertiary }}>{EMPTY}</span>;
}

interface LogsTableProps {
  logs: LogEntry[];
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  // Collapse identical messages into a single row with an occurrence Count column
  // (Error Monitor). Off by default so Log Monitor shows every log individually.
  groupByMessage?: boolean;
}

// Fetch the next batch once the user scrolls within this many px of the bottom.
const SCROLL_THRESHOLD = 200;

// While the table is actively scrolling we hide any open tooltip: MUI's popper
// doesn't fire mouseleave on wheel/drag scroll, so it lingers and detaches from
// its (now-moved) anchor. We toggle this class on <body> imperatively — no React
// re-render — and a GlobalStyles rule hides tooltip poppers while it's set.
const SCROLLING_CLASS = "logs-table-scrolling";
// How long after the last scroll event before tooltips are allowed back.
const SCROLL_IDLE_MS = 150;

export default function LogsTable({
  logs,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  groupByMessage = false,
}: LogsTableProps) {
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  // Grouped view (Error Monitor): the clicked group whose occurrences the modal lists.
  const [modalRow, setModalRow] = useState<DisplayRow | null>(null);
  // When grouping, default to the most frequent message first; otherwise newest first.
  const [orderBy, setOrderBy] = useState<string>(groupByMessage ? "count" : "timestamp");
  const [order, setOrder] = useState<Order>("desc");

  // Columns to render — when grouping, drop Timestamp (not meaningful once
  // identical messages collapse) and insert the Count column right after Message.
  const columns = useMemo(() => {
    if (!groupByMessage) return COLUMNS;
    const cols = COLUMNS.filter((c) => c.field !== "timestamp");
    const idx = cols.findIndex((c) => c.field === "message");
    cols.splice(idx + 1, 0, COUNT_COLUMN);
    // Give Tags more room so a 3rd tag fits before the "+N" overflow chip.
    return cols.map((c) =>
      c.field === "filteredTags" ? { ...c, width: 260 } : c
    );
  }, [groupByMessage]);

  // Error Monitor (grouped) uses a larger, more readable type scale; Log Monitor
  // keeps its original compact sizes.
  const fs = {
    head: groupByMessage ? "0.82rem" : "0.7rem",
    cell: groupByMessage ? "0.9rem" : "0.78rem",
    chip: groupByMessage ? "0.72rem" : "0.62rem",
    code: groupByMessage ? "0.85rem" : "0.75rem",
  };

  // How many tag chips to show before collapsing the rest into "+N".
  const maxTags = groupByMessage ? 3 : 2;
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Make sure we don't leave the scroll class stuck on <body> if we unmount
  // mid-scroll (e.g. filters re-fetch while a tooltip was hidden).
  useEffect(
    () => () => {
      clearTimeout(scrollIdleTimer.current);
      document.body.classList.remove(SCROLLING_CLASS);
    },
    []
  );

  // Build the rows to render: one per log, or one per unique message when grouping.
  // The representative log is the most recent occurrence in each message group.
  const rows = useMemo<DisplayRow[]>(() => {
    const list = logs || [];
    if (!groupByMessage) return list.map((log) => ({ log, count: 1, logs: [log] }));
    const groups = new Map<string, DisplayRow>();
    for (const log of list) {
      // Group on the cleaned core message so rows that differ only by request id /
      // url / body collapse together.
      const key = cleanMessage(log.message);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { log, count: 1, logs: [log] });
      } else {
        existing.count += 1;
        existing.logs.push(log);
        if ((log.timestamp || "") > (existing.log.timestamp || "")) existing.log = log;
      }
    }
    return Array.from(groups.values());
  }, [logs, groupByMessage]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = rowValue(a, orderBy);
      const bv = rowValue(b, orderBy);
      let c = 0;
      if (av < bv) c = -1;
      else if (av > bv) c = 1;
      return order === "asc" ? c : -c;
    });
    return arr;
  }, [rows, orderBy, order]);

  // Trigger loadMore near the bottom; the hook guards against overlapping/duplicate
  // fetches, so calling it on every qualifying scroll event is safe.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Hide tooltips while scrolling; re-enable once the scroll goes idle.
    document.body.classList.add(SCROLLING_CLASS);
    clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = setTimeout(() => {
      document.body.classList.remove(SCROLLING_CLASS);
    }, SCROLL_IDLE_MS);

    if (!hasMore || loadingMore || !onLoadMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
      onLoadMore();
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          [`body.${SCROLLING_CLASS} .MuiTooltip-popper`]: {
            display: "none !important",
          },
        }}
      />
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: tokens.divider,
          borderRadius: "4px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <TableContainer sx={{ maxHeight: 600 }} onScroll={handleScroll}>
          <Table
            size="small"
            stickyHeader
            sx={{
              fontSize: fs.cell,
              "& .MuiTableCell-root": {
                borderColor: tokens.divider,
                py: groupByMessage ? 0.9 : 0.5,
                fontSize: fs.cell,
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ "& .MuiTableCell-root": { bgcolor: tokens.elevated } }}>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sortDirection={orderBy === col.field ? order : false}
                    sx={{
                      width: col.width,
                      fontWeight: 700,
                      fontSize: fs.head,
                      color: tokens.textSecondary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.field}
                        direction={orderBy === col.field ? order : "asc"}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row, i) => {
                const log = row.log;
                const m: LogMetadata = log.metadata || {};
                const errCode = (m.statusCode || m.errorCode || "") as string | number;
                const levelSev = SEVERITY[severityForLevel(log.level)];
                const codeSev = SEVERITY[severityForCode(errCode)];
                const tags = log.filteredTags || [];
                const serviceLabel = formatService(log.service);
                // Subtle severity row tint (faint, derived from the severity color)
                // so a cluster of errors/warnings is scannable at a glance — softer
                // than the old solid wash. Other rows keep neutral zebra.
                const tintBase =
                  log.level === "ERROR"
                    ? SEVERITY.error.text
                    : log.level === "WARN" || log.level === "WARNING"
                    ? SEVERITY.warning.text
                    : null;
                const baseBg = tintBase
                  ? alpha(tintBase, 0.08)
                  : i % 2 === 1
                  ? tokens.zebra
                  : tokens.surface;
                const hoverBg = tintBase ? alpha(tintBase, 0.16) : tokens.hover;
                return (
                  <TableRow
                    hover
                    key={log.id || i}
                    onClick={() =>
                      groupByMessage ? setModalRow(row) : setSelectedLog(log)
                    }
                    sx={{
                      cursor: "pointer",
                      bgcolor: baseBg,
                      "&:hover": { bgcolor: hoverBg },
                    }}
                  >
                    {!groupByMessage && (
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontFamily: tokens.mono,
                          color: tokens.textSecondary,
                        }}
                      >
                        {formatTimestamp(log.timestamp) || <EmptyCell />}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={levelLabel(log.level)}
                        size="small"
                        sx={{
                          bgcolor: levelSev.bg,
                          color: levelSev.text,
                          fontWeight: 700,
                          fontSize: fs.chip,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: tokens.textPrimary, maxWidth: 120 }}>
                      {serviceLabel ? (
                        <Tooltip title={serviceLabel} placement="top-start">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontSize: fs.cell, color: tokens.textPrimary }}
                          >
                            {serviceLabel}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <EmptyCell />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: tokens.textPrimary }}>
                      {formatEnv(log.environment) || <EmptyCell />}
                    </TableCell>
                    <TableCell
                      sx={
                        // Cap the Message column so it doesn't dominate the row;
                        // text ellipsis-truncates and the full line stays in the tooltip.
                        groupByMessage ? { maxWidth: 360 } : { maxWidth: 280 }
                      }
                    >
                      {log.message ? (
                        // Tooltip keeps the full raw line; the cell shows the cleaned
                        // core message when grouping (Error Monitor).
                        <Tooltip title={log.message} placement="top-start">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontSize: fs.cell, color: tokens.textPrimary }}
                          >
                            {groupByMessage ? cleanMessage(log.message) : log.message}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <EmptyCell />
                      )}
                    </TableCell>
                    {groupByMessage && (
                      <TableCell>
                        <Chip
                          label={row.count}
                          size="small"
                          sx={{
                            bgcolor: row.count > 1 ? codeSev.bg : SEVERITY.neutral.bg,
                            color: row.count > 1 ? codeSev.text : SEVERITY.neutral.text,
                            fontWeight: 700,
                            fontSize: fs.chip,
                            fontFamily: tokens.mono,
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      {errCode ? (
                        <span
                          style={{
                            fontFamily: tokens.mono,
                            fontSize: fs.code,
                            color: codeSev.text,
                          }}
                        >
                          {errCode}
                        </span>
                      ) : (
                        <EmptyCell />
                      )}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {log.supplier ? (
                        <Chip
                          label={log.supplier}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: tokens.divider,
                            color: tokens.textSecondary,
                            textTransform: "capitalize",
                          }}
                        />
                      ) : (
                        <EmptyCell />
                      )}
                    </TableCell>
                    <TableCell>
                      {tags.length > 0 ? (
                        <Box sx={{ display: "flex", gap: 0.4, flexWrap: "nowrap", overflow: "hidden" }}>
                          {tags.slice(0, maxTags).map((t, idx) => (
                            <Tooltip key={idx} title={t} placement="top-start">
                              <Chip
                                label={t}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: tokens.divider,
                                  color: tokens.textSecondary,
                                  maxWidth: 80,
                                }}
                              />
                            </Tooltip>
                          ))}
                          {tags.length > maxTags && (
                            <Tooltip title={tags.join(", ")} placement="top-start">
                              <Chip
                                label={`+${tags.length - maxTags}`}
                                size="small"
                                sx={{ bgcolor: SEVERITY.neutral.bg, color: SEVERITY.neutral.text }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <EmptyCell />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ color: tokens.textSecondary, py: 3 }}
                  >
                    No logs to display.
                  </TableCell>
                </TableRow>
              )}
              {loadingMore && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: tokens.textSecondary,
                      }}
                    >
                      <CircularProgress size={16} />
                      <span>Loading more…</span>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.5,
            py: 0.75,
            borderTop: `1px solid ${tokens.divider}`,
            color: tokens.textSecondary,
            fontSize: "0.72rem",
          }}
        >
          <span>
            {groupByMessage
              ? `${sorted.length} unique messages (${logs.length} errors total)`
              : `${sorted.length} logs loaded`}
          </span>
          {!hasMore && sorted.length > 0 && <span>End of logs</span>}
          {hasMore && <span>Scroll to load more</span>}
        </Box>
      </Paper>
      <LogDetailDrawer log={selectedLog} onClose={() => setSelectedLog(null)} />
      {groupByMessage && (
        <LogDetailModal
          logs={modalRow?.logs || null}
          title={modalRow ? cleanMessage(modalRow.log.message) : undefined}
          onClose={() => setModalRow(null)}
        />
      )}
    </>
  );
}
